import { useState } from "react";
import {
  Modal,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Chip,
  Box,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { IoMdCloudUpload, IoMdInformationCircleOutline } from "react-icons/io";
import { FaFile, FaTrash } from "react-icons/fa";
import {
  DropzoneCard,
  ModalContent,
  FileList,
  FileItem,
  DeleteButton,
} from "./UploadDocumentsModal.styles";
import { useDocuments } from "../../hooks/useDocuments.js";
import { useModalState } from "../../hooks/useModalState.js";

const MAX_FILE_SIZE_MB = Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 25;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_FORMATS = {
  // Images
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/svg+xml": [".svg"],
  // Documents
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  // Audio
  "audio/mpeg": [".mp3"],
  "audio/wav": [".wav"],
  "audio/ogg": [".ogg"],
  // Video
  "video/mp4": [".mp4"],
  "video/webm": [".webm"],
  "video/quicktime": [".mov"],
};

const FORMAT_GROUPS = [
  { label: "Imágenes", exts: [".jpg", ".jpeg", ".png", ".webp", ".svg"] },
  { label: "Documentos", exts: [".pdf", ".doc", ".docx", ".xls", ".xlsx"] },
  { label: "Audio", exts: [".mp3", ".wav", ".ogg"] },
  { label: "Video", exts: [".mp4", ".webm", ".mov"] },
];

export const UploadDocumentsModal = ({ onSave, openButton }) => {
  const { open, openModal, closeModal } = useModalState();
  const [files, setFiles] = useState([]);
  const { uploadFiles, isUploading } = useDocuments();
  const [showFormats, setShowFormats] = useState(false);

  const [sizeError, setSizeError] = useState("");

  const onFileZoneChange = (newFiles) => {
    setSizeError("");
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const {
    getRootProps,
    getInputProps,
    open: openFileDialog,
  } = useDropzone({
    onDrop: onFileZoneChange,
    noClick: true,
    maxSize: MAX_FILE_SIZE_BYTES,
    accept: ACCEPTED_FORMATS,
    onDropRejected: (rejectedFiles) => {
      const names = rejectedFiles.map(({ file }) => file.name).join(", ");
      const hasSizeError = rejectedFiles.some(({ errors }) =>
        errors.some((e) => e.code === "file-too-large")
      );
      const hasTypeError = rejectedFiles.some(({ errors }) =>
        errors.some((e) => e.code === "file-invalid-type")
      );

      if (hasSizeError)
        setSizeError(`${names} supera el límite de ${MAX_FILE_SIZE_MB} MB`);
      if (hasTypeError) setSizeError(`${names} tiene un formato no permitido`);
    },
  });

  const handleSave = async () => {
    const newFiles = await uploadFiles(files);
    const filesWithNames = newFiles.map((file, index) => ({
      ...file,
      name: files[index].name,
    }));
    onSave(filesWithNames);
    setFiles([]);
    closeModal();
  };

  const handleDelete = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <>
      <Stack onClick={openModal}>{openButton}</Stack>
      <Modal open={open} onClose={closeModal}>
        <ModalContent>
          <Typography variant="h6" component="h2" mb={2}>
            Subir documentos
          </Typography>
          <DropzoneCard
            justifyContent="center"
            alignItems="center"
            onClick={(e) => e.stopPropagation()}
            {...getRootProps()}
          >
            <input hidden {...getInputProps()} />
            <IoMdCloudUpload
              color="#00b936"
              style={{
                width: "40px",
                height: "40px",
              }}
            />
            <Typography color="black" fontSize="14px">
              Arrastra tus documentos aquí
            </Typography>
            <Typography
              style={{ textDecoration: "underline", cursor: "pointer" }}
              fontSize="12px"
              color="primary.dark"
              onClick={(e) => {
                e.stopPropagation();
                openFileDialog();
              }}
            >
              Elegir archivos
            </Typography>
          </DropzoneCard>
          {sizeError && (
            <Typography color="error" fontSize="12px" mt={1}>
              {sizeError}
            </Typography>
          )}
          <Stack mt={1} alignItems="center" width="100%">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="320px"
            >
              <Typography
                fontSize="11px"
                color="primary.dark"
                sx={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={() => setShowFormats((prev) => !prev)}
              >
                {showFormats
                  ? "Ocultar formatos aceptados"
                  : "Ver formatos aceptados"}
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{
                  border: "0.5px solid",
                  borderColor: "divider",
                  borderRadius: "6px",
                  px: 1,
                  py: 0.25,
                  backgroundColor: "grey.50",
                }}
              >
                <IoMdInformationCircleOutline
                  style={{ fontSize: 12, color: "inherit" }}
                />
                <Typography fontSize="11px" color="text.secondary">
                  Máx.{" "}
                  <Box component="span" fontWeight={500} color="text.primary">
                    {MAX_FILE_SIZE_MB} MB
                  </Box>{" "}
                  por archivo
                </Typography>
              </Stack>
            </Stack>

            {/* Formatos expandidos */}
            {showFormats && (
              <Stack mt={1} spacing={0.75} width="100%" maxWidth={320}>
                {FORMAT_GROUPS.map(({ label, exts }) => (
                  <Stack
                    key={label}
                    direction="row"
                    alignItems="flex-start"
                    gap={0.5}
                  >
                    <Typography
                      fontSize="11px"
                      color="text.secondary"
                      minWidth="80px"
                      sx={{ flexShrink: 0 }}
                    >
                      {label}:
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {exts.map((ext) => (
                        <Chip
                          key={ext}
                          label={ext}
                          size="small"
                          sx={{
                            fontSize: "10px",
                            height: "20px",
                            backgroundColor: "#f5f5f5",
                            color: "#616161",
                          }}
                        />
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>

          <Stack mt={2}>
            {files?.length > 0 && (
              <FileList>
                {files.map((file, index) => (
                  <FileItem key={index}>
                    <FaFile style={{ marginRight: "8px", color: "#00b936" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        flexGrow: 1,
                        mr: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ mr: 1 }}>
                      {`${(file.size / 1024).toFixed(2)} KB`}
                    </Typography>
                    <DeleteButton onClick={() => handleDelete(index)}>
                      <FaTrash />
                    </DeleteButton>
                  </FileItem>
                ))}
              </FileList>
            )}
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button
              variant="contained"
              onClick={closeModal}
              sx={{
                backgroundColor: "#fff",
                color: "#000",
                border: "0.5px #000",
                borderRadius: "4px",
                boxShadow: "2px 2px 4px rgba(0, 0, 0.0, 0.4)",
                "&:hover": {
                  backgroundColor: "#D5D8DC",
                  color: "#fff",
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={files?.length === 0 || isUploading}
              color="primary"
              sx={{
                boxShadow: "2px 2px 4px rgba(0, 0, 0.0, 0.4)",
              }}
            >
              {isUploading && <CircularProgress size="20px" />}
              Agregar
            </Button>
          </Stack>
        </ModalContent>
      </Modal>
    </>
  );
};
